import { useCallback, useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card, Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { ConfirmDialog } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { RoomCategory } from "../api/types";

// Round 48 — Bot Rooms testing feature (routes/admin/bot-rooms.js). A test
// room is a real live room hosted by its own bot account, flagged is_test:
// it shows a TEST badge in the app, gifts into it are rejected, and it is
// never auto-ended. Deleting it also deletes every bot created for it.
interface BotRoom {
  id: number;
  roomId: number;
  title: string;
  maxSeats: number;
  isLive: boolean;
  categoryName: string | null;
  avatarUrl: string | null;
  botCount: number;
  seatedCount: number;
  createdAt: string;
}

export function BotRoomsPage() {
  const { showToast } = useToast();
  const [rooms, setRooms] = useState<BotRoom[]>([]);
  const [categories, setCategories] = useState<RoomCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [botCount, setBotCount] = useState(5);
  const [maxSeats, setMaxSeats] = useState(8);
  const [isCreating, setIsCreating] = useState(false);

  const [adjust, setAdjust] = useState<Record<number, number>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BotRoom | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await apiRequest<{ rooms: BotRoom[] }>("/admin/bot-rooms");
      setRooms(res.rooms);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load test rooms");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    apiRequest<RoomCategory[]>("/admin/room-categories")
      .then((c) => setCategories(Array.isArray(c) ? c : []))
      .catch(() => setCategories([]));
  }, [reload]);

  async function createRoom() {
    if (!title.trim()) {
      showToast("Enter a room name", "error");
      return;
    }
    setIsCreating(true);
    try {
      const res = await apiRequest<{ botCount: number; failed: string[] }>("/admin/bot-rooms", {
        method: "POST",
        body: {
          title: title.trim(),
          categoryId: categoryId ? Number(categoryId) : null,
          avatarUrl: avatarUrl.trim() || null,
          botCount,
          maxSeats,
        },
      });
      showToast(
        res.failed.length ? `Created with ${res.botCount} bot(s); ${res.failed.length} bot(s) failed` : `Test room created with ${res.botCount} bot(s)`,
        res.failed.length ? "error" : "success"
      );
      setTitle("");
      setAvatarUrl("");
      await reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not create test room", "error");
    } finally {
      setIsCreating(false);
    }
  }

  async function run(room: BotRoom, fn: () => Promise<string>) {
    setBusyId(room.id);
    try {
      showToast(await fn(), "success");
      await reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Action failed", "error");
    } finally {
      setBusyId(null);
    }
  }

  const countFor = (room: BotRoom) => Math.max(1, adjust[room.id] ?? 1);

  return (
    <AppShell title="Bot Rooms (Testing)">
      <p className="mb-4 text-xs text-ink-faint">
        Test rooms are hosted by bot accounts and are marked TEST in the app. Gifts to bots or into these rooms are
        rejected, so no real coins, rankings or user data are affected. Deleting a room also deletes its bot accounts.
      </p>

      <Card className="mb-6 p-4">
        <h3 className="mb-3 text-sm font-semibold text-ink">Create Test Room</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <Input label="Room name (max 18)" maxLength={18} value={title} onChange={(e) => setTitle(e.target.value)} />
          <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">No category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Input label="Room DP (https image URL, optional)" value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} />
          <Input label="Bots in room (0-30)" type="number" min={0} max={30} value={botCount} onChange={(e) => setBotCount(Math.max(0, Math.min(30, Number(e.target.value))))} />
          <Input label="Seats (1-20)" type="number" min={1} max={20} value={maxSeats} onChange={(e) => setMaxSeats(Math.max(1, Math.min(20, Number(e.target.value))))} />
        </div>
        <div className="mt-3">
          <Button onClick={createRoom} isLoading={isCreating}>
            Create Test Room
          </Button>
        </div>
      </Card>

      {isLoading && <p className="text-sm text-ink-faint">Loading…</p>}
      {error && <p className="text-sm text-danger">{error}</p>}
      {!isLoading && !error && rooms.length === 0 && <p className="text-sm text-ink-faint">No test rooms yet.</p>}

      <div className="grid gap-4">
        {rooms.map((room) => (
          <Card key={room.id} className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Avatar src={room.avatarUrl} className="h-12 w-12 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {room.title} <Badge tone="gold">TEST</Badge> <Badge tone={room.isLive ? "success" : "neutral"}>{room.isLive ? "Live" : "Disabled"}</Badge>
                </p>
                <p className="text-xs text-ink-faint">
                  Room ID {room.roomId} · {room.categoryName ?? "No category"} · {room.botCount} bot(s) · {room.seatedCount}/{room.maxSeats} seats
                </p>
              </div>
              <Input
                className="w-20"
                type="number"
                min={1}
                value={countFor(room)}
                onChange={(e) => setAdjust((p) => ({ ...p, [room.id]: Math.max(1, Number(e.target.value)) }))}
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={busyId === room.id}
                onClick={() =>
                  run(room, async () => {
                    const r = await apiRequest<{ added: number }>(`/admin/bot-rooms/${room.id}/bots`, { method: "POST", body: { count: countFor(room) } });
                    return `Added ${r.added} bot(s)`;
                  })
                }
              >
                Add bots
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={busyId === room.id}
                onClick={() =>
                  run(room, async () => {
                    const r = await apiRequest<{ removed: number }>(`/admin/bot-rooms/${room.id}/bots`, { method: "DELETE", body: { count: countFor(room) } });
                    return `Removed ${r.removed} bot(s)`;
                  })
                }
              >
                Remove bots
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={busyId === room.id}
                onClick={() =>
                  run(room, async () => {
                    await apiRequest(`/admin/bot-rooms/${room.id}/status`, { method: "POST", body: { enabled: !room.isLive } });
                    return room.isLive ? "Room disabled" : "Room enabled";
                  })
                }
              >
                {room.isLive ? "Disable" : "Enable"}
              </Button>
              <Button size="sm" variant="danger" disabled={busyId === room.id} onClick={() => setPendingDelete(room)}>
                Delete
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete test room?"
        description={`This deletes "${pendingDelete?.title ?? ""}" and every bot account created for it.`}
        confirmLabel="Delete"
        isDangerous
        isLoading={busyId !== null}
        onConfirm={async () => {
          const room = pendingDelete;
          if (!room) return;
          await run(room, async () => {
            await apiRequest(`/admin/bot-rooms/${room.id}`, { method: "DELETE" });
            return "Test room deleted";
          });
          setPendingDelete(null);
        }}
      />
    </AppShell>
  );
}
